import { CalendarClock, Loader2, Sparkles, X } from "lucide-react";
import { useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { toast } from "sonner";

import {
  getCampaignGenerationReviewActionState,
  type ChatGeneratedAssetBatchLike,
} from "@/lib/assistant-chat-media-gallery";
import {
  getAssistantCampaignGenerationProposalStorageKey,
  getAssistantCampaignRemainingAssetCount,
  loadAssistantCampaignGenerationProposalState,
  saveAssistantCampaignGenerationProposalState,
  type AssistantCampaignGenerationChoice,
  type AssistantCampaignGenerationPreviewReference,
} from "@/lib/assistant-campaign-generation-previews";
import { trpc } from "@/lib/trpc";
import { Badge, Button } from "@runcheetah/ui";

export function AssistantCampaignGenerationPreviews({
  brandId,
  sourceChatId,
  references,
  isStale = false,
  upgradeLabel,
  onUpgradeRequired,
  campaignBatches = [],
}: {
  brandId: string;
  sourceChatId?: string | null;
  references: AssistantCampaignGenerationPreviewReference[];
  isStale?: boolean;
  upgradeLabel?: string;
  onUpgradeRequired?: () => void;
  campaignBatches?: ChatGeneratedAssetBatchLike[];
}) {
  if (references.length === 0) {
    return null;
  }

  return (
    <div className="w-full space-y-3">
      {references.map((reference) => (
        <AssistantCampaignGenerationPreviewBanner
          key={reference.id}
          brandId={brandId}
          sourceChatId={sourceChatId}
          reference={reference}
          isStale={isStale}
          upgradeLabel={upgradeLabel}
          onUpgradeRequired={onUpgradeRequired}
          campaignBatches={campaignBatches}
        />
      ))}
    </div>
  );
}

function AssistantCampaignGenerationPreviewBanner({
  brandId,
  sourceChatId,
  reference,
  isStale,
  upgradeLabel,
  onUpgradeRequired,
  campaignBatches,
}: {
  brandId: string;
  sourceChatId?: string | null;
  reference: AssistantCampaignGenerationPreviewReference;
  isStale: boolean;
  upgradeLabel?: string;
  onUpgradeRequired?: () => void;
  campaignBatches: ChatGeneratedAssetBatchLike[];
}) {
  const intl = useIntl();
  const utils = trpc.useUtils();
  const proposalStorageKey = getAssistantCampaignGenerationProposalStorageKey(
    brandId,
    reference.id,
  );
  const [proposalState, setProposalState] = useState(() =>
    loadAssistantCampaignGenerationProposalState(proposalStorageKey),
  );
  const [error, setError] = useState<string | null>(null);
  const cancelled = proposalState.status === "cancelled";
  const created = proposalState.status === "created";
  const closed = proposalState.status === "active" && isStale;
  const linkedCampaignBatch = campaignBatches.find(
    (batch) => batch.sourceType === "campaign" && batch.sourceProposalId === reference.id,
  );
  const reviewActionState = getCampaignGenerationReviewActionState({
    batches: campaignBatches,
    proposalId: reference.id,
  });
  const shouldShowReviewActions =
    created && proposalState.generationMode === "preview_first" && Boolean(reviewActionState);
  const campaignGenerated = Boolean(
    linkedCampaignBatch &&
    linkedCampaignBatch.pendingTaskCount === 0 &&
    linkedCampaignBatch.generatedAssetCount > 0 &&
    (linkedCampaignBatch.status === "active" || linkedCampaignBatch.status === "generated"),
  );
  const campaignFailed = linkedCampaignBatch?.status === "failed";
  const requestedAssetCount = reference.request.items.length;
  const remainingAssetCount = getAssistantCampaignRemainingAssetCount(reference.request);
  const previewRequest = { ...reference.request, generationMode: "preview_first" as const };
  const allAssetsRequest = { ...reference.request, generationMode: "all_assets" as const };
  const previewQuoteQuery = trpc.campaign.quoteCreateOnDemand.useQuery(
    { brandId, request: previewRequest },
    { enabled: !closed && !cancelled && !created, retry: false },
  );
  const allAssetsQuoteQuery = trpc.campaign.quoteCreateOnDemand.useQuery(
    { brandId, request: allAssetsRequest },
    { enabled: !closed && !cancelled && !created, retry: false },
  );
  const approveQuoteQuery = trpc.campaign.quoteApprove.useQuery(
    { brandId, id: reviewActionState?.campaignId ?? "00000000-0000-4000-8000-000000000000" },
    { enabled: shouldShowReviewActions, retry: false },
  );
  const previewRegenerationQuoteQuery = trpc.campaign.quotePreviewRegeneration.useQuery(
    { brandId, id: reviewActionState?.campaignId ?? "00000000-0000-4000-8000-000000000000" },
    {
      enabled: shouldShowReviewActions && reviewActionState?.canRegeneratePreview === true,
      retry: false,
    },
  );
  const createCampaign = trpc.campaign.createOnDemand.useMutation({
    onSuccess: async (data, variables) => {
      const generationMode = variables.request.generationMode ?? "preview_first";
      const quote =
        generationMode === "all_assets" ? allAssetsQuoteQuery.data : previewQuoteQuery.data;
      const nextState = {
        status: "created" as const,
        workerJobId: data.workerJobId,
        generationMode,
        ...(typeof quote?.runsRequired === "number" ? { runsRequired: quote.runsRequired } : {}),
      };
      saveAssistantCampaignGenerationProposalState(proposalStorageKey, nextState);
      setProposalState(nextState);
      setError(null);
      await Promise.all([
        utils.campaign.list.invalidate({ brandId }),
        utils.campaign.nextGenerationCheck.invalidate({ brandId }),
        sourceChatId
          ? utils.brandAssistantChat.generatedAssets.invalidate({ brandId, chatId: sourceChatId })
          : Promise.resolve(),
        utils.brand.getBillingOverview.invalidate({ brandId }),
      ]);
      toast.success(
        intl.formatMessage({
          id: "assistantCampaignGenerationPreview.createSuccess",
          defaultMessage: "Campaign generation queued",
        }),
      );
    },
    onError: (mutationError) => setError(mutationError.message),
  });
  const invalidateCampaignReviewData = async (campaignId: string) => {
    await Promise.all([
      utils.campaign.list.invalidate({ brandId }),
      utils.campaign.get.invalidate({ brandId, id: campaignId }),
      utils.campaign.nextGenerationCheck.invalidate({ brandId }),
      sourceChatId
        ? utils.brandAssistantChat.generatedAssets.invalidate({ brandId, chatId: sourceChatId })
        : Promise.resolve(),
      utils.brand.getBillingOverview.invalidate({ brandId }),
    ]);
  };
  const approveCampaign = trpc.campaign.approve.useMutation({
    onSuccess: async (_, variables) => {
      const nextRunsRequired = addRunsToProposalState(
        proposalState,
        approveQuoteQuery.data?.runsRequired,
      );
      if (nextRunsRequired) {
        saveAssistantCampaignGenerationProposalState(proposalStorageKey, nextRunsRequired);
        setProposalState(nextRunsRequired);
      }
      setError(null);
      await invalidateCampaignReviewData(variables.id);
      toast.success(
        intl.formatMessage(
          {
            id: "assistantCampaignGenerationPreview.approveSuccess",
            defaultMessage:
              "{count, plural, one {Campaign approved. Generating # remaining asset.} other {Campaign approved. Generating # remaining assets.}}",
          },
          { count: remainingAssetCount },
        ),
      );
    },
    onError: (mutationError) => setError(mutationError.message),
  });
  const regeneratePreview = trpc.campaign.regeneratePreview.useMutation({
    onSuccess: async (_, variables) => {
      const nextRunsRequired = addRunsToProposalState(
        proposalState,
        previewRegenerationQuoteQuery.data?.runsRequired,
      );
      if (nextRunsRequired) {
        saveAssistantCampaignGenerationProposalState(proposalStorageKey, nextRunsRequired);
        setProposalState(nextRunsRequired);
      }
      setError(null);
      await invalidateCampaignReviewData(variables.id);
      toast.success(
        intl.formatMessage({
          id: "assistantCampaignGenerationPreview.regenerateSuccess",
          defaultMessage: "Campaign preview regeneration queued.",
        }),
      );
    },
    onError: (mutationError) => setError(mutationError.message),
  });
  const visibleError =
    error ??
    previewQuoteQuery.error?.message ??
    allAssetsQuoteQuery.error?.message ??
    approveQuoteQuery.error?.message ??
    previewRegenerationQuoteQuery.error?.message ??
    null;
  const previewHasRuns = hasEnoughRuns(previewQuoteQuery.data ?? null);
  const allAssetsHasRuns = hasEnoughRuns(allAssetsQuoteQuery.data ?? null);
  const canGeneratePreview = Boolean(
    previewQuoteQuery.data && previewHasRuns && !closed && !cancelled && !created,
  );
  const canGenerateAll = Boolean(
    allAssetsQuoteQuery.data && allAssetsHasRuns && !closed && !cancelled && !created,
  );

  const confirm = (generationMode: AssistantCampaignGenerationChoice) => {
    if (createCampaign.isPending) {
      return;
    }
    const request = generationMode === "all_assets" ? allAssetsRequest : previewRequest;
    createCampaign.mutate({
      brandId,
      request,
      sourceChatId: sourceChatId ?? undefined,
      sourceProposalId: reference.id,
    });
  };

  const approveRunsAvailable = Boolean(
    approveQuoteQuery.data && hasEnoughRuns(approveQuoteQuery.data),
  );
  const regenerateRunsAvailable = Boolean(
    previewRegenerationQuoteQuery.data && hasEnoughRuns(previewRegenerationQuoteQuery.data),
  );
  const approveRunsSuffix = formatRunsSuffix(approveQuoteQuery.data?.runsRequired, intl);
  const regenerateRunsSuffix = formatRunsSuffix(
    previewRegenerationQuoteQuery.data?.runsRequired,
    intl,
  );

  if (cancelled) {
    return (
      <div className="w-full rounded-3xl border border-border/70 bg-muted/35 px-4 py-3 text-sm text-muted-foreground">
        <FormattedMessage
          id="assistantCampaignGenerationPreview.cancelled"
          defaultMessage="Campaign proposal cancelled."
        />
      </div>
    );
  }

  return (
    <article className="w-full border-y border-border/60 bg-transparent">
      <div className="space-y-4 p-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0 space-y-1">
            <div className="flex items-center gap-2 text-sm font-medium text-foreground/88">
              <span className="flex h-7 w-7 shrink-0 items-center justify-center text-muted-foreground">
                <Sparkles className="h-3.5 w-3.5" aria-hidden="true" />
              </span>
              <FormattedMessage
                id="assistantCampaignGenerationPreview.title"
                defaultMessage="Generate Campaign?"
              />
            </div>
            <p className="max-w-2xl text-sm leading-6 text-muted-foreground">
              {reference.summary ?? reference.request.prompt}
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Badge
              variant="outline"
              className="rounded-md border-border/70 bg-card/70 text-muted-foreground"
            >
              <CalendarClock className="mr-1 h-3.5 w-3.5" aria-hidden="true" />
              <FormattedMessage
                id="assistantCampaignGenerationPreview.dailySchedule"
                defaultMessage="Daily schedule"
              />
            </Badge>
          </div>
        </div>

        {!closed && !created && visibleError ? (
          <div className="rounded-2xl border border-destructive/25 bg-destructive/10 px-4 py-3 text-sm text-destructive">
            {visibleError}
          </div>
        ) : null}
        {!closed && !created && (!previewHasRuns || !allAssetsHasRuns) ? (
          <div className="rounded-2xl border border-destructive/25 bg-destructive/10 px-4 py-3 text-sm text-destructive">
            <FormattedMessage
              id="assistantCampaignGenerationPreview.insufficientRuns"
              defaultMessage="One or more generation choices need more runs than remain."
            />
          </div>
        ) : null}

        {closed ? (
          <ProposalStatus>
            <X className="text-muted-foreground/40" size="16" aria-hidden="true" />
            <span className="font-medium text-foreground/58">
              <FormattedMessage
                id="assistantCampaignGenerationPreview.supersededLabel"
                defaultMessage="This proposal can no longer be accepted because the conversation has moved forward."
              />
            </span>
          </ProposalStatus>
        ) : created ? (
          <div className="space-y-3 border-t border-border/60 pt-3">
            {visibleError ? (
              <div className="rounded-2xl border border-destructive/25 bg-destructive/10 px-4 py-3 text-sm text-destructive">
                {visibleError}
              </div>
            ) : null}
            <CampaignGenerationDetails
              reference={reference}
              generationMode={proposalState.generationMode}
            />
            <ProposalStatus>
              {campaignFailed ? (
                <>
                  <X className="text-destructive/60" size="16" aria-hidden="true" />
                  <span className="font-medium text-foreground/58">
                    <FormattedMessage
                      id="assistantCampaignGenerationPreview.failedLabel"
                      defaultMessage="Failed"
                    />
                    :
                  </span>
                  <span className="min-w-0 text-destructive/80">
                    <FormattedMessage
                      id="assistantCampaignGenerationPreview.failedGeneration"
                      defaultMessage="Campaign generation failed"
                    />
                  </span>
                </>
              ) : shouldShowReviewActions ? (
                <>
                  <Sparkles className="text-muted-foreground/40" size="16" aria-hidden="true" />
                  <FormattedMessage
                    id="assistantCampaignGenerationPreview.previewReadyStatus"
                    defaultMessage="Campaign Preview Ready"
                  />
                </>
              ) : campaignGenerated ? (
                <>
                  <Sparkles className="text-muted-foreground/40" size="16" aria-hidden="true" />
                  <span className="font-medium text-foreground/58">
                    <FormattedMessage
                      id="assistantCampaignGenerationPreview.generatedCampaign"
                      defaultMessage="Campaign Generated"
                    />
                    :
                  </span>
                  <span className="min-w-0 text-muted-foreground">
                    <FormattedMessage
                      id="assistantCampaignGenerationPreview.generatedCampaignAssets"
                      defaultMessage="{count, plural, one {Generated # campaign asset} other {Generated # campaign assets}}"
                      values={{ count: requestedAssetCount }}
                    />
                  </span>
                </>
              ) : (
                <>
                  <Sparkles className="text-muted-foreground/40" size="16" aria-hidden="true" />
                  <span className="min-w-0 bg-[linear-gradient(90deg,var(--muted-foreground)_0%,var(--muted-foreground)_34%,var(--primary)_50%,var(--muted-foreground)_66%,var(--muted-foreground)_100%)] bg-[length:220%_100%] bg-clip-text text-transparent motion-safe:animate-[assistant-status-aurora_2.2s_linear_infinite]">
                    <FormattedMessage
                      id="assistantCampaignGenerationPreview.generatingCampaign"
                      defaultMessage="Generating Campaign"
                    />
                  </span>
                  <span className="font-medium text-foreground/42">:</span>
                  <span className="min-w-0 bg-[linear-gradient(90deg,var(--muted-foreground)_0%,var(--muted-foreground)_34%,var(--primary)_50%,var(--muted-foreground)_66%,var(--muted-foreground)_100%)] bg-[length:220%_100%] bg-clip-text text-transparent motion-safe:animate-[assistant-status-aurora_2.2s_linear_infinite]">
                    {!linkedCampaignBatch ? (
                      <FormattedMessage
                        id="assistantCampaignGenerationPreview.generationPhasePlanning"
                        defaultMessage="Planning"
                      />
                    ) : proposalState.generationMode === "all_assets" ? (
                      <FormattedMessage
                        id="assistantCampaignGenerationPreview.generationPhaseAssets"
                        defaultMessage="{count, plural, one {# campaign asset} other {# campaign assets}}"
                        values={{ count: requestedAssetCount }}
                      />
                    ) : linkedCampaignBatch.status === "active" ? (
                      <FormattedMessage
                        id="assistantCampaignGenerationPreview.generationPhaseRemainingAssets"
                        defaultMessage="{count, plural, one {# remaining campaign asset} other {# remaining campaign assets}}"
                        values={{ count: remainingAssetCount }}
                      />
                    ) : (
                      <FormattedMessage
                        id="assistantCampaignGenerationPreview.generationPhasePreview"
                        defaultMessage="Campaign preview"
                      />
                    )}
                  </span>
                </>
              )}
              {getTotalRunsUsed(proposalState) !== null ? (
                <RunsUsed count={getTotalRunsUsed(proposalState) ?? 0} />
              ) : null}
            </ProposalStatus>
            {shouldShowReviewActions && reviewActionState ? (
              <div className="flex flex-col gap-2 rounded-2xl border border-border/60 bg-background/45 p-3 sm:flex-row sm:items-center sm:justify-between">
                <p className="text-sm text-muted-foreground">
                  <FormattedMessage
                    id="assistantCampaignGenerationPreview.reviewReady"
                    defaultMessage="The campaign preview is ready. Generate {count, plural, one {the remaining campaign asset} other {the remaining # campaign assets}} when you're happy with it, or regenerate the preview once."
                    values={{ count: remainingAssetCount }}
                  />
                </p>
                <div className="flex shrink-0 flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                  <Button
                    type="button"
                    variant="outline"
                    className="rounded-full"
                    disabled={
                      !reviewActionState.canRegeneratePreview ||
                      !regenerateRunsAvailable ||
                      regeneratePreview.isPending ||
                      approveCampaign.isPending
                    }
                    onClick={() =>
                      regeneratePreview.mutate({ brandId, id: reviewActionState.campaignId })
                    }
                  >
                    {regeneratePreview.isPending ? (
                      <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                    ) : null}
                    <FormattedMessage
                      id="assistantCampaignGenerationPreview.regeneratePreview"
                      defaultMessage="Regenerate Campaign Preview{runs}"
                      values={{ runs: regenerateRunsSuffix }}
                    />
                  </Button>
                  <Button
                    type="button"
                    className="rounded-full"
                    disabled={
                      !reviewActionState.canApprove ||
                      !approveRunsAvailable ||
                      approveCampaign.isPending ||
                      regeneratePreview.isPending
                    }
                    onClick={() =>
                      approveCampaign.mutate({ brandId, id: reviewActionState.campaignId })
                    }
                  >
                    {approveCampaign.isPending ? (
                      <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                    ) : null}
                    <FormattedMessage
                      id="assistantCampaignGenerationPreview.approve"
                      defaultMessage="Generate {count, plural, one {# Remaining Campaign Asset} other {# Remaining Campaign Assets}}{runs}"
                      values={{ count: remainingAssetCount, runs: approveRunsSuffix }}
                    />
                  </Button>
                </div>
              </div>
            ) : null}
          </div>
        ) : (
          <div className="space-y-3 border-t border-border/60 pt-3">
            <p className="text-sm text-muted-foreground">
              <FormattedMessage
                id="assistantCampaignGenerationPreview.choicePrompt"
                defaultMessage="How do you want to generate it?"
              />
            </p>
            <div className="flex shrink-0 flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <Button
                type="button"
                variant="ghost"
                className="rounded-full text-foreground/65"
                disabled={createCampaign.isPending}
                onClick={() => {
                  saveAssistantCampaignGenerationProposalState(proposalStorageKey, {
                    status: "cancelled",
                  });
                  setProposalState({ status: "cancelled" });
                }}
              >
                <X className="h-4 w-4" aria-hidden="true" />
                <FormattedMessage id="common.cancel" defaultMessage="Cancel" />
              </Button>
              {onUpgradeRequired && (!previewHasRuns || !allAssetsHasRuns) ? (
                <Button type="button" className="rounded-full" onClick={onUpgradeRequired}>
                  {upgradeLabel ?? (
                    <FormattedMessage id="runConfirmation.upgrade" defaultMessage="Upgrade" />
                  )}
                </Button>
              ) : null}
              <Button
                type="button"
                variant="outline"
                className="rounded-full"
                disabled={!canGeneratePreview || createCampaign.isPending}
                onClick={() => confirm("preview_first")}
              >
                {createCampaign.isPending &&
                createCampaign.variables?.request.generationMode === "preview_first" ? (
                  <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                ) : null}
                <FormattedMessage
                  id="assistantCampaignGenerationPreview.generatePreviewFirst"
                  defaultMessage="Generate Campaign Preview{runs}"
                  values={{ runs: formatRunsSuffix(previewQuoteQuery.data?.runsRequired, intl) }}
                />
              </Button>
              <Button
                type="button"
                className="rounded-full"
                disabled={!canGenerateAll || createCampaign.isPending}
                onClick={() => confirm("all_assets")}
              >
                {createCampaign.isPending &&
                createCampaign.variables?.request.generationMode === "all_assets" ? (
                  <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                ) : null}
                <FormattedMessage
                  id="assistantCampaignGenerationPreview.generateAll"
                  defaultMessage="Generate Campaign{runs}"
                  values={{ runs: formatRunsSuffix(allAssetsQuoteQuery.data?.runsRequired, intl) }}
                />
              </Button>
            </div>
          </div>
        )}
      </div>
    </article>
  );
}

function hasEnoughRuns(quote: { runsRemaining: number | null; runsRequired: number } | null) {
  return quote?.runsRemaining === null || !quote || quote.runsRemaining >= quote.runsRequired;
}

function getTotalRunsUsed(
  state: ReturnType<typeof loadAssistantCampaignGenerationProposalState>,
): number | null {
  if (state.status !== "created" || typeof state.runsRequired !== "number") {
    return null;
  }

  return state.runsRequired + (state.additionalRunsUsed ?? 0);
}

function addRunsToProposalState(
  state: ReturnType<typeof loadAssistantCampaignGenerationProposalState>,
  runsRequired: number | undefined,
): ReturnType<typeof loadAssistantCampaignGenerationProposalState> | null {
  if (state.status !== "created" || typeof runsRequired !== "number" || runsRequired <= 0) {
    return null;
  }

  return {
    ...state,
    additionalRunsUsed: (state.additionalRunsUsed ?? 0) + runsRequired,
  };
}

function formatRunsSuffix(runsRequired: number | undefined, intl: ReturnType<typeof useIntl>) {
  if (runsRequired === undefined) {
    return "";
  }

  return intl.formatMessage(
    {
      id: "assistantCampaignGenerationPreview.runsSuffix",
      defaultMessage: " ({count, plural, =0 {free} one {# run} other {# runs}})",
    },
    { count: runsRequired },
  );
}

function CampaignGenerationDetails({
  reference,
  generationMode,
}: {
  reference: AssistantCampaignGenerationPreviewReference;
  generationMode: AssistantCampaignGenerationChoice;
}) {
  const firstScheduledDate = reference.firstScheduledFor
    ? new Intl.DateTimeFormat(undefined, { month: "short", day: "numeric" }).format(
        new Date(reference.firstScheduledFor),
      )
    : "-";

  return (
    <div className="grid gap-2 rounded-2xl border border-border/60 bg-background/45 p-3 text-xs text-muted-foreground sm:grid-cols-2">
      <CampaignDetailItem
        label={
          <FormattedMessage
            id="assistantCampaignGenerationPreview.directionLabel"
            defaultMessage="Direction"
          />
        }
        value={reference.request.prompt}
        className="sm:col-span-2"
      />
      <CampaignDetailItem
        label={
          <FormattedMessage
            id="assistantCampaignGenerationPreview.scheduleLabel"
            defaultMessage="Schedule"
          />
        }
        value={
          <FormattedMessage
            id="assistantCampaignGenerationPreview.scheduleValue"
            defaultMessage="{count, plural, one {# post} other {# posts}} from {date}"
            values={{ count: reference.request.items.length, date: firstScheduledDate }}
          />
        }
      />
      <CampaignDetailItem
        label={
          <FormattedMessage
            id="assistantCampaignGenerationPreview.formatsLabel"
            defaultMessage="Formats"
          />
        }
        value={reference.formats.map((format) => format.replaceAll("_", " ")).join(", ")}
      />
      <CampaignDetailItem
        label={
          <FormattedMessage
            id="assistantCampaignGenerationPreview.modeLabel"
            defaultMessage="Generation"
          />
        }
        value={
          generationMode === "all_assets" ? (
            <FormattedMessage
              id="assistantCampaignGenerationPreview.modeAllAssets"
              defaultMessage="All assets now"
            />
          ) : (
            <FormattedMessage
              id="assistantCampaignGenerationPreview.modePreviewFirst"
              defaultMessage="First asset preview"
            />
          )
        }
      />
    </div>
  );
}

function CampaignDetailItem({
  label,
  value,
  className,
}: {
  label: React.ReactNode;
  value: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={className}>
      <div className="font-semibold uppercase tracking-[0.16em] text-muted-foreground/70">
        {label}
      </div>
      <div className="mt-1 line-clamp-3 text-foreground/78">{value}</div>
    </div>
  );
}

function ProposalStatus({ children }: { children: React.ReactNode }) {
  return (
    <div className="text-sm text-muted-foreground">
      <div className="flex min-w-0 flex-wrap items-center gap-1.5">{children}</div>
    </div>
  );
}

function RunsUsed({ count }: { count: number }) {
  return (
    <>
      <span className="h-1 w-1 rounded-full bg-muted-foreground/42" aria-hidden="true" />
      <span className="font-medium text-foreground/58">
        <FormattedMessage
          id="assistantCampaignGenerationPreview.runsUsed"
          defaultMessage="{count, plural, =0 {Free} one {# run used} other {# runs used}}"
          values={{ count }}
        />
      </span>
    </>
  );
}
